import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ApiKeyModalComponent } from './api-key-modal-component/api-key-modal-component.component';
import { IpModalComponentComponent } from './ip-modal-component/ip-modal-component.component';
import { Provider, ProviderService, Company } from 'src/app/swagger';
import { EditorCompanyComponent } from 'src/app/managers/manage-companies/editor-company/editor-company.component';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentMode } from 'src/app/classes/provider';

@Component({
  selector: 'app-provider-detail',
  templateUrl: './provider-detail.component.html',
  styleUrls: ['./provider-detail.component.scss'],
})
export class ProviderDetailComponent implements OnInit {
  form: FormGroup;

  public companyFieldConfigs = [
    { key: 'vatCode', labelKey: 'vatCode' },
    { key: 'taxCode', labelKey: 'taxCode' },
    { key: 'businessName', labelKey: 'businessName' },
    { key: 'paymentUniqueCode', labelKey: 'paymentUniqueCode' },
    { key: 'address', labelKey: 'address' },
    { key: 'cap', labelKey: 'cap' },
    { key: 'city', labelKey: 'City' },
    { key: 'prov', labelKey: 'prov' },
    { key: 'nation_iso', labelKey: 'nation_iso' },
    { key: 'phone', labelKey: 'phone' },
    { key: 'emailPec', labelKey: 'emailPec' },
    { key: 'email', labelKey: 'Email' },
  ];

  @Input() provider: Provider;
  @Input() providerUpdated: EventEmitter<Provider>;
  @Input() closeEvent: EventEmitter<void>;
  @Input() isReadOnly;
  @Input() isNew;

  public paymentModes = Object.keys(PaymentMode)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      label: key,
      value: PaymentMode[key as keyof typeof PaymentMode],
    }));

  public localProvider: Provider;

  constructor(
    private modalController: ModalController,
    private providerManager: ProviderService,
    private _modalController: ModalController,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    const emptyCompany: Company = {
      vatCode: '',
      taxCode: '',
      businessName: '',
      paymentUniqueCode: '',
      address: '',
      cap: '',
      city: '',
      prov: '',
      nation_iso: '',
      phone: '',
      email: '',
      emailPec: '',
    };

    this.localProvider = {
      ...(this.provider || {}),
      company: (() => {
        const hasCompany = this.provider && (this.provider as any).company;
        return this.isNew || !hasCompany ? { ...emptyCompany } : (this.provider as any).company;
      })(),
    };

    this.form = this.fb.group({
      name: [
        { value: this.localProvider.name, disabled: this.isReadOnly },
        [Validators.required, Validators.minLength(2), Validators.maxLength(100)],
      ],
      paymentMode: [
        { value: this.localProvider.paymentMode, disabled: this.isReadOnly },
        Validators.required,
      ],
      company: this.fb.group({
        vatCode: [
          { value: this.localProvider.company.vatCode, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.pattern(/^\d{11}$/)] : [],
        ],
        taxCode: [
          { value: this.localProvider.company.taxCode, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.pattern(/^[A-Z0-9]{16}$/i)] : [],
        ],
        businessName: [
          { value: this.localProvider.company.businessName, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.minLength(2), Validators.maxLength(100)] : [],
        ],
        paymentUniqueCode: [
          { value: this.localProvider.company.paymentUniqueCode, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.maxLength(50)] : [],
        ],
        address: [
          { value: this.localProvider.company.address, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.maxLength(100)] : [],
        ],
        cap: [
          { value: this.localProvider.company.cap, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.pattern(/^\d{5}$/)] : [],
        ],
        city: [
          { value: this.localProvider.company.city, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.maxLength(50)] : [],
        ],
        prov: [
          { value: this.localProvider.company.prov, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.pattern(/^[A-Z]{2}$/i)] : [],
        ],
        nation_iso: [
          { value: this.localProvider.company.nation_iso, disabled: this.isReadOnly },
          this.isNew ? [Validators.required, Validators.pattern(/^[A-Z]{2}$/)] : [],
        ],
        phone: [
          { value: this.localProvider.company.phone, disabled: this.isReadOnly },
          this.isNew
            ? [Validators.required, Validators.pattern(/^[+0-9\s]{8,15}$/)]
            : [],
        ],
        email: [
          { value: this.localProvider.company.email, disabled: this.isReadOnly },
          this.isNew
            ? [Validators.required, Validators.email]
            : [Validators.email],
        ],
        emailPec: [
          { value: this.localProvider.company.emailPec, disabled: this.isReadOnly },
          this.isNew
            ? [Validators.required, Validators.email]
            : [Validators.email],
        ],
      }),
    });

    console.log(this.localProvider);
  }

  onSave() {
    if (this.form.get('name').invalid) {
      this.form.get('name').markAsTouched();
    }

    if (this.isNew && this.form.get('company').invalid) {
      this.markFormGroupTouched(this.form.get('company') as FormGroup);
    }

    if (this.form.invalid) return;

    const updatedProvider: Provider = {
      ...this.localProvider,
      ...this.form.getRawValue(),
    };

    if (this.providerUpdated) {
      this.providerUpdated.emit(updatedProvider);
    }

    this.close();
  }

  getValidationErrors(control: AbstractControl | null): string[] {
    if (!control || !control.errors || !control.touched) return [];
  
    const errors = control.errors;
    const messages: string[] = [];
  
    if (errors['required']) {
      messages.push('validators.required');
    }
    if (errors['email']) {
      messages.push('validators.email');
    }
    if (errors['minlength']) {
      messages.push('validators.minlength');
    }
    if (errors['maxlength']) {
      messages.push('validators.maxlength');
    }
    if (errors['pattern']) {
      messages.push('validators.pattern');
    }
  
    return messages;
  }

  onEdit() {
    this.isReadOnly = false;
    this.form.enable();
  }

  close() {
    if (this.closeEvent) {
      this.closeEvent.emit();
    }
  }

  getPaymentModeLabel(mode: PaymentMode): string {
    const entry = this.paymentModes.find((pm) => pm.value === mode);
    return entry ? `enum.paymentMode.${entry.label}` : '';
  }

  private markFormGroupTouched(group: FormGroup) {
    Object.values(group.controls).forEach((control) => {
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  async showApiKeyModal() {
    const modal = await this.modalController.create({
      component: ApiKeyModalComponent,
      componentProps: {
        apiKey: this.localProvider.apiKey,
        providerId: this.localProvider.id,
        isReadOnly: this.isReadOnly,
      },
      backdropDismiss: false,
    });

    await modal.present();

    modal.onDidDismiss().then((data) => {
      if (data) {
        this.localProvider.apiKey = data.data;
      }
    });
  }

  async showIpModal() {
    const modal = await this.modalController.create({
      component: IpModalComponentComponent,
      componentProps: {
        allowedIps: this.localProvider.allowedIps,
        providerId: this.localProvider.id,
        isReadOnly: this.isReadOnly,
      },
      backdropDismiss: false,
    });

    await modal.present();

    const { data, role } = await modal.onDidDismiss();

    if (role === 'confirm' && data.updated) {
      this.providerManager.getProviderById(parseInt(this.localProvider.id)).subscribe({
        next: (provider) => {
          this.localProvider = provider;
        },
        error: (err) => {
          console.error('Error updating provider', err);
        },
      });
    }
  }

  public async onClick() {
    console.log(this.localProvider.company);
    this.openEditor(this.localProvider.company);
  }

  public async openEditor(obj?: Company) {
    const popover = await this._modalController.create({
      component: EditorCompanyComponent,
      componentProps: {
        companyId: obj ? obj.id : null,
        isReadOnly: this.isReadOnly,
      }
    });

    await popover.present();
  }
}
